import * as React from "react";
import { DropdownItemProps } from "semantic-ui-react";
import { getFeatureToggles } from "@teamprevent/common/getFeatureToggles";
import * as FeatureToggleNames from "@teamprevent/common/featureToggleNames";

import { initialWorkAdjustmentQuestionnaireMasterConfig } from "../../workAdjustment/initialWorkAdjustmentQuestionnaireMasterConfig";
import { updateWorkAdjustmentQuestionnaireMasterConfig } from "../../workAdjustment/updateWorkAdjustmentQuestionnaireMasterConfig";
import { DropdownQuestion as DropdownQuestionComponent } from "../components/questions/DropdownQuestion";
import { TextQuestion as TextQuestionComponent } from "../components/questions/TextQuestion";
import { DropdownOption } from "../model";

import { masterConfig as referralMasterConfig } from "./referral/masterConfig";
import { masterConfig as ppqManagerSectionMasterConfig } from "./ppqManagerSection/masterConfig";
import { masterConfig as ppqEmployeeSectionMasterConfig } from "./ppqEmployeeSection/masterConfig";
import { masterConfig as physiotherapyMgrReferralMasterConfig } from "./physiotherapyMgrReferral/masterConfig";
import { masterConfig as physiotherapySelfReferralMasterConfig } from "./physiotherapySelfReferral/masterConfig";
import { masterConfig as sraManagerSectionMasterConfig } from "./ppqManagerSection/masterConfig";
import { masterConfig as sraEmployeeSectionMasterConfig } from "./ppqEmployeeSection/masterConfig";
import { masterConfig as termsAndConditionsOfUseMasterConfig } from "./termsAndConditionsOfUse/masterConfig";
import { masterConfig as genericCaseMasterConfig } from "./genericCase/masterConfig";

export const AdditionalQuestionsSectionId = "AdditionalQuestions";
export const FurtherQuestionsSectionTitle = "Further Questions";

export interface CustomQuestionConfig {
    id: string;
    questionText: string;
    questionElement?: React.ReactNode;
    disabled?: false;
    questionType?: QuestionType;
    options?: string[];
    removedOptions?: string[];
    isMandatory?: boolean;
}

export enum QuestionType {
    TextQuestion = "TextQuestion",
    DropdownQuestion = "DropdownQuestion"
}

export const questionTypeOptions: DropdownItemProps[] = [
    { value: QuestionType.TextQuestion, text: "Free Text" },
    { value: QuestionType.DropdownQuestion, text: "Dropdown" }
];

export const getQuestionTypeText = (question: CustomQuestionConfig) => {
    if (!question.questionType) {
        // Legacy questions are TextQuestions
        return questionTypeOptions.find(o => o.value === QuestionType.TextQuestion)?.text;
    }

    return questionTypeOptions.find(o => o.value === question.questionType)?.text;
};

export const renderAdditionalQuestions = (additionalQuestions: CustomQuestionConfig[], readonly: boolean, hideDivider: boolean) => {
    if (additionalQuestions?.length > 0) {
        return (<>
            {additionalQuestions.map(question => {
                if (question.questionType === QuestionType.DropdownQuestion) {
                    const currentOptions: DropdownOption[] = (question.options ?? []).map(o => ({ text: o, value: o }));
                    const oldOptions: DropdownOption[] = (question.removedOptions ?? []).map(o => ({ text: o, value: o, old: true }));
                    const options = currentOptions.concat(oldOptions);
                    return (
                        <DropdownQuestionComponent
                            key={ question.id }
                            questionConfig={question}
                            readonly={readonly}
                            mandatory={question.isMandatory}
                            clearable={true}
                            options={options}
                            filterOldOptions={true}
                            hideDivider={hideDivider}
                        />
                    );
                }

                return (
                    <TextQuestionComponent
                        key={question.id}
                        questionConfig={question}
                        readonly={readonly}
                        mandatory={question.isMandatory}
                        hideDivider={hideDivider}
                    />
                );
            })}
        </>);
    }

    return null;
};

export interface DisabledQuestionConfig {
    id: string;
    disabled: true;
    isMandatory?: boolean;
}

export type QuestionConfig = CustomQuestionConfig | DisabledQuestionConfig

export const isQuestionDisabled = (questionConfig: QuestionConfig): questionConfig is DisabledQuestionConfig => {
    return questionConfig && (questionConfig as DisabledQuestionConfig).disabled === true;
};

export const getQuestionConfig = (questionConfigs: QuestionConfig[], questionId: string) =>
    questionConfigs.find(q => q.id === questionId) || { id: questionId, disabled: true };

export const isQuestionMandatory = (questionnaireConfig: QuestionnaireConfig, questionId: string, defaultValue: boolean = true): boolean => {
    const questionConfig = questionnaireConfig.questions.find(c => c.id === questionId);

    if (questionConfig)
    {
        const customConfig = (questionConfig as CustomQuestionConfig);

        if (customConfig.isMandatory !== null && customConfig.isMandatory !== undefined) {
            return customConfig.isMandatory;
        }
    }

    return defaultValue;
};

export type MasterQuestionConfig = QuestionConfig & {
    canBeDisabled: boolean;
    questionText: string;
};

export interface QuestionnaireConfig {
    questions: QuestionConfig[];
    additionalQuestions: CustomQuestionConfig[] | undefined;
}

export type QuestionnaireConfigState = {
    [key in ConfigurableQuestionnaireKey]?: QuestionnaireConfig;
};

export type QuestionnaireTypes = {
    [key in ConfigurableQuestionnaireKey]: {
        displayName: string;
        masterConfig: MasterQuestionConfig[];
        preventAdditionalQuestions?: boolean;
    };
};

export type ConfigurableQuestionnaireKey =
    "referral" | "ppqManagerSection" | "ppqEmployeeSection" | "physiotherapyMgrReferral" |
    "physiotherapySelfReferral" | "initialWorkAdjustmentQuestionnaire" | "updateWorkAdjustmentQuestionnaire" |
    "sraManagerSection" | "sraEmployeeSection" | "termsAndConditionsOfUse" | "genericCase";

export const questionnaireTypes: QuestionnaireTypes = {
    referral: { displayName: "Referral Form", masterConfig: referralMasterConfig },
    ppqManagerSection: { displayName: "PPQ (Manager Section)", masterConfig: ppqManagerSectionMasterConfig },
    ppqEmployeeSection: { displayName: "PPQ (Employee Section)", masterConfig: ppqEmployeeSectionMasterConfig },
    physiotherapyMgrReferral: { displayName: "Physiotherapy MGR Referral", masterConfig: physiotherapyMgrReferralMasterConfig },
    physiotherapySelfReferral: { displayName: "Physiotherapy Self Referral", masterConfig: physiotherapySelfReferralMasterConfig },
    genericCase: { displayName: "Request Other Services Form", masterConfig: genericCaseMasterConfig,
        preventAdditionalQuestions: !getFeatureToggles()[FeatureToggleNames.MoreSectionsForGenericForm] },
    initialWorkAdjustmentQuestionnaire: { displayName: "Initial Work Adjustment Questionnaire", masterConfig: initialWorkAdjustmentQuestionnaireMasterConfig },
    updateWorkAdjustmentQuestionnaire: { displayName: "Update Work Adjustment Questionnaire", masterConfig: updateWorkAdjustmentQuestionnaireMasterConfig },
    sraManagerSection: { displayName: "Stress Risk Assessment Manager", masterConfig: sraManagerSectionMasterConfig },
    sraEmployeeSection: { displayName: "Stress Risk Assessment Employee", masterConfig: sraEmployeeSectionMasterConfig },
    termsAndConditionsOfUse:
        { displayName: "Terms and conditions of use", masterConfig: termsAndConditionsOfUseMasterConfig, preventAdditionalQuestions: true }
};
